import React, { forwardRef, useCallback } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { Box, Typography } from '@mui/material';
import { useModal } from '@store/hooks';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import {
  ELIG_TOKENS,
  getReadinessTreatment,
} from '../theme/readinessTokens';
import type { BlockReadiness } from '../types/readiness';

import type { EligibilitySource } from './EligibilitySummaryContent';
import { EligibilityPopover } from './EligibilityPopover';
import { ReadinessIndicator } from './ReadinessIndicator';

export type EligibilityBadgeVariant = 'dot' | 'chip';

export interface EligibilityBadgeProps {
  patientId?: string;
  encounterId?: string;
  patientName?: string;
  source: EligibilitySource;
  /**
   * Visual treatment.
   *   `dot`   → readiness dot, with an optional inline action verb when
   *             actionable (suppress with `showActionLabel={false}` for
   *             dense surfaces — see calendar block, appointment card).
   *   `chip`  → readiness verb chip with leading dot. Use for surfaces where
   *             a single-patient verb earns its space (note metadata).
   * Default: `dot`.
   */
  variant?: EligibilityBadgeVariant;
  /**
   * For the `dot` variant: whether to show the inline action label
   * ("Collect $30", "Add insurance") next to the dot when the readiness
   * state is actionable. Default `true`.
   *
   * Set `false` on dense surfaces where the dot alone is sufficient and
   * the popover does the talking — calendar blocks, appointment cards.
   * Ignored when `variant="chip"`.
   */
  showActionLabel?: boolean;
  /**
   * Suppress the hover popover entirely. Click-to-drawer still works.
   *
   * Use on surfaces that already have their own hover affordance (e.g.
   * the calendar block, where react-big-calendar shows an event tooltip
   * on hover) so the eligibility popover doesn't compete or stack.
   *
   * Default `false`.
   */
  disablePopover?: boolean;
}

/**
 * Eligibility badge. Renders a dot or verb chip based on `variant`, wrapped
 * in EligibilityPopover for hover-peek + state-appropriate actions. Verbs
 * and supporting copy are BE-owned (`readiness_label`, `summary`); the FE
 * never fabricates them.
 *
 * Hidden states:
 *   - feature gate off / tutorial mode
 *   - readiness=null while hasCoverage=true (overnight cron pending; user
 *     should not see a "Run check" CTA per P2/P18 of the eligibility brief)
 *
 * Verified state still renders — a filled green dot tells the user the
 * system is on top of the visit.
 */
export const EligibilityBadge: React.FC<EligibilityBadgeProps> = ({
  patientId,
  encounterId,
  patientName,
  source,
  variant = 'dot',
  showActionLabel = true,
  disablePopover = false,
}) => {
  const {
    isHidden,
    readiness,
    readinessLabel,
    hasCoverage,
    eligibilityData,
  } = useEligibilityBadgeState({ patientId, encounterId });
  const { open: openModal } = useModal();

  const openDrawer = useCallback(() => {
    openModal(MODAL_TYPES.ELIGIBILITY_DETAIL, {
      patientId,
      encounterId,
      patientName,
      source,
    });
  }, [openModal, patientId, encounterId, patientName, source]);

  // Click is the badge's own affordance. stopPropagation prevents the
  // surrounding surface (calendar event open, appointment-card edit) from
  // also firing when the user targets the badge specifically.
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      openDrawer();
    },
    [openDrawer],
  );

  // Nothing to render against — surfaces like the calendar pass undefined IDs
  // for events without an assigned patient.
  if (!patientId && !encounterId) return null;
  if (isHidden) return null;
  // Wait for data — without this, hasCoverage defaults to false on the first
  // render and we briefly flash an "Add insurance" verb for every patient
  // before the query resolves.
  if (!eligibilityData) return null;
  // Auto-run hasn't returned yet — no marker. Per P2 the user reviews
  // exceptions, never triggers the check themselves.
  if (readiness === null && hasCoverage) return null;

  const trackingMeta = { patient_id: patientId, encounter_id: encounterId, source };

  // Only actionable states surface a label next to the dot — verified /
  // collected / null are quiet ("system is on it", nothing to do).
  // Dense surfaces opt out of the inline label entirely.
  const actionLabel = showActionLabel
    ? deriveActionLabel({ readiness, readinessLabel, hasCoverage })
    : null;

  if (disablePopover) {
    // Bare trigger — no hover popover wrapper. Click still opens drawer.
    return variant === 'chip' ? (
      <ChipTrigger
        readiness={readiness}
        label={readinessLabel}
        hasCoverage={hasCoverage}
        onClick={handleClick}
      />
    ) : (
      <DotTrigger
        readiness={readiness}
        hasCoverage={hasCoverage}
        actionLabel={actionLabel}
        onClick={handleClick}
      />
    );
  }

  return (
    <EligibilityPopover
      patientId={patientId}
      encounterId={encounterId}
      patientName={patientName}
      trackingMeta={trackingMeta}
      onExpand={openDrawer}
    >
      {({ ref, triggerProps }) =>
        variant === 'chip' ? (
          <ChipTrigger
            ref={ref}
            readiness={readiness}
            label={readinessLabel}
            hasCoverage={hasCoverage}
            triggerProps={triggerProps}
            onClick={handleClick}
          />
        ) : (
          <DotTrigger
            ref={ref}
            readiness={readiness}
            hasCoverage={hasCoverage}
            actionLabel={actionLabel}
            triggerProps={triggerProps}
            onClick={handleClick}
          />
        )
      }
    </EligibilityPopover>
  );
};

function deriveActionLabel({
  readiness,
  readinessLabel,
  hasCoverage,
}: {
  readiness: BlockReadiness | null;
  readinessLabel: string | null;
  hasCoverage: boolean;
}): string | null {
  if (!hasCoverage) return 'Add insurance';
  if (readiness === 'verified' || readiness === 'collected' || readiness === null) {
    return null;
  }
  return readinessLabel;
}

// ─── Triggers ────────────────────────────────────────────────────────────────

interface DotTriggerProps {
  readiness: BlockReadiness | null;
  hasCoverage: boolean;
  /** Compact action verb shown next to the dot. Null for verified/collected
   *  (quiet states). For action states, this is the BE's `readiness_label`
   *  (e.g. "Collect $30", "Verify with payer") or "Add insurance" for the
   *  no-coverage case. */
  actionLabel: string | null;
  triggerProps?: Record<string, unknown>;
  /** Click commits to the deep-dive drawer. Hover lifecycle for the popover
   *  is handled by triggerProps. */
  onClick?: (event: React.MouseEvent) => void;
}

const DotTrigger = forwardRef<HTMLElement, DotTriggerProps>(
  ({ readiness, hasCoverage, actionLabel, triggerProps, onClick }, ref) => {
    const labelColor = readiness
      ? getReadinessTreatment(readiness)?.color ?? ELIG_TOKENS.gray
      : ELIG_TOKENS.gray;

    return (
      <Box
        ref={ref}
        component="span"
        {...triggerProps}
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.625,
          cursor: 'pointer',
          py: 0.25,
          px: actionLabel ? 0.625 : 0.25,
          borderRadius: actionLabel ? 1 : '50%',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        {readiness ? (
          <ReadinessIndicator readiness={readiness} size={10} />
        ) : (
          // No-coverage marker: hollow gray dot.
          <Box
            role="img"
            aria-label={hasCoverage ? 'Eligibility unknown' : 'No insurance on file'}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: `2px solid ${ELIG_TOKENS.gray}`,
              bgcolor: 'transparent',
              flexShrink: 0,
            }}
          />
        )}
        {actionLabel && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: labelColor,
              fontWeight: 500,
              fontSize: 13,
              lineHeight: 1,
              textDecoration: 'underline',
              textDecorationThickness: '1.5px',
              textUnderlineOffset: '2px',
            }}
          >
            {actionLabel}
          </Typography>
        )}
      </Box>
    );
  },
);
DotTrigger.displayName = 'DotTrigger';

interface ChipTriggerProps {
  readiness: BlockReadiness | null;
  label: string | null;
  hasCoverage: boolean;
  triggerProps?: Record<string, unknown>;
  onClick?: (event: React.MouseEvent) => void;
}

const ChipTrigger = forwardRef<HTMLElement, ChipTriggerProps>(
  ({ readiness, label, hasCoverage, triggerProps, onClick }, ref) => {
    // No-coverage falls back to a stable verb; the popover handles the action.
    if (!hasCoverage) {
      return (
        <ChipShell ref={ref} triggerProps={triggerProps} onClick={onClick}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              border: `2px solid ${ELIG_TOKENS.gray}`,
              flexShrink: 0,
            }}
          />
          <ChipLabel color={ELIG_TOKENS.gray} underline>
            Add insurance
          </ChipLabel>
        </ChipShell>
      );
    }

    if (!readiness || !label) return null;

    const treatment = getReadinessTreatment(readiness);
    if (!treatment) return null;
    const isVerified = readiness === 'verified';

    return (
      <ChipShell ref={ref} triggerProps={triggerProps} onClick={onClick}>
        <ReadinessIndicator readiness={readiness} size={8} />
        <ChipLabel color={treatment.color} underline={!isVerified} bold={!isVerified}>
          {label}
        </ChipLabel>
      </ChipShell>
    );
  },
);
ChipTrigger.displayName = 'ChipTrigger';

interface ChipShellProps {
  triggerProps?: Record<string, unknown>;
  onClick?: (event: React.MouseEvent) => void;
  children: React.ReactNode;
}

const ChipShell = forwardRef<HTMLElement, ChipShellProps>(
  ({ triggerProps, onClick, children }, ref) => (
    <Box
      ref={ref}
      component="span"
      {...triggerProps}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.25,
        px: 0.75,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
      }}
    >
      {children}
    </Box>
  ),
);
ChipShell.displayName = 'ChipShell';

interface ChipLabelProps {
  color: string;
  underline?: boolean;
  bold?: boolean;
  children: React.ReactNode;
}

const ChipLabel: React.FC<ChipLabelProps> = ({ color, underline, bold, children }) => (
  <Typography
    variant="body2"
    component="span"
    sx={{
      color,
      fontWeight: bold ? 600 : 400,
      fontSize: 13,
      ...(underline
        ? {
            textDecoration: 'underline',
            textDecorationThickness: '1.5px',
          }
        : {}),
    }}
  >
    {children}
  </Typography>
);
